const GEMINI_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.0-flash';
const SCORING_MODE = (process.env.REACT_APP_SCORING_MODE || 'moderate').toLowerCase();
const GEMINI_FALLBACK_MODEL = process.env.REACT_APP_GEMINI_FALLBACK_MODEL || 'gemini-2.5-flash';
const GEMINI_TIMEOUT_MS = parseInt(process.env.REACT_APP_GEMINI_TIMEOUT_MS || '55000');

function getApiUrl(model = GEMINI_MODEL) {
  return `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${GEMINI_KEY}`;
}

/* ═══════════════════════════════════════════════════════════════════
   SYSTEM PROMPT
   ═══════════════════════════════════════════════════════════════════ */

const SYSTEM_PROMPT = `You are an expert AI Prompt Engineering Evaluator working for an enterprise organization. Your role is to assess employees' prompt engineering skills as part of a company-wide AI readiness program.

EVALUATION STANDARDS:
- You evaluate written prompts that employees craft for real-world business scenarios.
- You are fair, consistent, and evidence-based in your scoring.
- You score based on demonstrated skill, not assumed intent.
- You MUST detect and flag AI-generated or copy-pasted responses.

SCORING MODE: ${SCORING_MODE.toUpperCase()}
${SCORING_MODE === 'strict' ? `- Rigorous standards. Deduct points for any missing element.
- Score above 80 = exceptional, production-ready prompts only.
- Vague or generic prompts score below 40 regardless of length.
- No partial credit for implied elements — must be explicitly stated.` : ''}
${SCORING_MODE === 'moderate' ? `- Fair professional standards. Give credit where effort is shown.
- Score above 80 = well-structured with most elements covered.
- Vague prompts below 50. Decent attempts with gaps score 50-70.
- Partial credit for elements implied but not explicit.` : ''}
${SCORING_MODE === 'lenient' ? `- Encouraging standards. Organization is early in AI adoption.
- Focus on what the employee did right first.
- Score above 60 = shows understanding of prompt structure.
- Give credit for effort and directional correctness.` : ''}

AI-GENERATED CONTENT DETECTION:
- MUST analyze whether the response is human-written or AI-generated/copied.
- AI signs: overly polished structure, perfect formatting, generic placeholder language, suspiciously comprehensive rubric coverage, corporate-speak without personal voice, template-like responses.
- Human signs: natural flow, personal voice, some imperfections, focused rather than perfectly comprehensive, practical over theoretical.
- Set ai_generated_flag true if detected and explain why.

Always respond in valid JSON only. No markdown, no preamble.`;

/* ═══════════════════════════════════════════════════════════════════
   API CALLER
   ONE attempt per model. NO retries.
   Failed call → null → mark pending → admin rescores later.
   This prevents retry cascades burning the RPM quota.
   ═══════════════════════════════════════════════════════════════════ */

async function callGemini(userPrompt, context = 'unknown') {
  if (!GEMINI_KEY) {
    console.error('Missing REACT_APP_GEMINI_API_KEY');
    return null;
  }

  // Primary first, then fallback — one attempt each
  const modelsToTry = [GEMINI_MODEL, GEMINI_FALLBACK_MODEL].filter(
    (m, i, arr) => arr.indexOf(m) === i
  );

  for (const model of modelsToTry) {
    const callStart = Date.now();
    const ts = () => new Date().toLocaleTimeString();
    try {
      console.log(`[${ts()}] ▶ START context=${context} model=${model}`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn(`[${ts()}] ⏱ TIMEOUT context=${context} model=${model} after ${GEMINI_TIMEOUT_MS/1000}s`);
      }, GEMINI_TIMEOUT_MS);

      let res;
      try {
        res = await fetch(getApiUrl(model), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
            contents: [{ parts: [{ text: userPrompt }] }],
            generationConfig: {
              temperature: 0.3,
              maxOutputTokens: 2000,
              responseMimeType: "application/json",
            },
          }),
        });
      } finally {
        clearTimeout(timeoutId);
      }

      const elapsed = ((Date.now() - callStart) / 1000).toFixed(1);

      if (!res.ok) {
        console.warn(`[${ts()}] ✗ HTTP ${res.status} context=${context} model=${model} took=${elapsed}s → trying fallback`);
        continue;
      }

      const data = await res.json();

      if (data.error) {
        console.error(`[${ts()}] ✗ API ERROR context=${context} model=${model} took=${elapsed}s:`, data.error.message || data.error);
        continue;
      }

      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (text) {
        console.log(`[${ts()}] ✓ OK context=${context} model=${model} took=${elapsed}s length=${text.length}`);
        return text;
      }

      if (data?.candidates?.[0]?.finishReason === 'SAFETY') {
        console.warn(`[${ts()}] ✗ SAFETY BLOCK context=${context} model=${model} took=${elapsed}s`);
        return null;
      }

      console.warn(`[${ts()}] ✗ EMPTY RESPONSE context=${context} model=${model} took=${elapsed}s → trying fallback`);

    } catch (e) {
      const elapsed = ((Date.now() - callStart) / 1000).toFixed(1);
      if (e.name === 'AbortError') {
        console.warn(`[${ts()}] ✗ TIMEOUT context=${context} model=${model} took=${elapsed}s → trying fallback`);
      } else {
        console.error(`[${ts()}] ✗ FETCH ERROR context=${context} model=${model} took=${elapsed}s:`, e.message);
      }
    }
  }

  console.error(`[${new Date().toLocaleTimeString()}] ✗ ALL FAILED context=${context} → marking pending`);
  return null;
}

/* ═══════════════════════════════════════════════════════════════════
   JSON PARSER
   ═══════════════════════════════════════════════════════════════════ */

function parseJSON(text) {
  if (!text) return null;
  try { return JSON.parse(text.trim()); } catch {}
  try {
    const cleaned = text.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '').trim();
    return JSON.parse(cleaned);
  } catch {}
  try { const m = text.match(/\{[\s\S]*\}/); if (m) return JSON.parse(m[0]); } catch {}
  console.error('JSON parse failed:', text.slice(0, 300));
  return null;
}

/* ═══════════════════════════════════════════════════════════════════
   PENDING RESULT — returned when scoring fails
   ═══════════════════════════════════════════════════════════════════ */

function pendingResult(raw = null) {
  return {
    score: null,
    feedback: 'AI scoring pending — admin can rescore from dashboard.',
    rubric_hits: [], rubric_misses: [], strengths: '', improvements: '',
    ai_generated_flag: false, ai_generated_reason: '', ai_generated_confidence: '',
    raw_ai_response: raw, scoring_pending: true,
  };
}

/* ═══════════════════════════════════════════════════════════════════
   SCORE WRITTEN PROMPT
   ═══════════════════════════════════════════════════════════════════ */

export async function scoreWrittenPrompt(scenario, response) {
  if (!GEMINI_KEY || !response?.trim()) return pendingResult();

  const prompt = `Score this employee's written prompt for an AI assistant.

SCENARIO GIVEN:
${scenario}

EMPLOYEE'S PROMPT:
"""
${response}
"""

Evaluate on these criteria (points shown):
- Role/Persona definition (0-15)
- Context provided (0-10)
- Task specificity (0-15)
- Output format (0-15)
- Tone/Voice (0-10)
- Constraints (0-15)
- Audience awareness (0-10)
- Examples/Structure (0-10)

Also detect: Is this response AI-generated or copy-pasted?

JSON response:
{
  "score": <0-100>,
  "feedback": "<2-3 sentence feedback>",
  "rubric_hits": ["<criterion met>"],
  "rubric_misses": ["<criterion missed>"],
  "strengths": "<what they did well>",
  "improvements": "<what to improve>",
  "ai_generated_flag": <true/false>,
  "ai_generated_reason": "<explanation or empty>",
  "ai_generated_confidence": "<high/medium/low>"
}`;

  const text = await callGemini(prompt, 'scorePrompt');
  if (!text) return pendingResult();

  const p = parseJSON(text);
  if (!p) return pendingResult(text);

  return {
    score: Math.min(Math.max(typeof p.score === 'number' ? p.score : 0, 0), 100),
    feedback: p.feedback || '',
    rubric_hits: p.rubric_hits || [],
    rubric_misses: p.rubric_misses || [],
    strengths: p.strengths || '',
    improvements: p.improvements || '',
    ai_generated_flag: p.ai_generated_flag || false,
    ai_generated_reason: p.ai_generated_reason || '',
    ai_generated_confidence: p.ai_generated_confidence || '',
    raw_ai_response: text,
    scoring_pending: false,
  };
}

/* ═══════════════════════════════════════════════════════════════════
   RESCORE — called from admin dashboard rescore button
   ═══════════════════════════════════════════════════════════════════ */

export async function rescoreScenario(scenarioText, response) {
  if (!GEMINI_KEY || !response?.trim()) return pendingResult();

  const prompt = `Score this employee's written prompt for an AI assistant.

SCENARIO GIVEN:
${scenarioText}

EMPLOYEE'S PROMPT:
"""
${response}
"""

Evaluate on these criteria (points shown):
- Role/Persona definition (0-15)
- Context provided (0-10)
- Task specificity (0-15)
- Output format (0-15)
- Tone/Voice (0-10)
- Constraints (0-15)
- Audience awareness (0-10)
- Examples/Structure (0-10)

Also detect: Is this response AI-generated or copy-pasted?

JSON response:
{
  "score": <0-100>,
  "feedback": "<2-3 sentence feedback>",
  "rubric_hits": ["<criterion met>"],
  "rubric_misses": ["<criterion missed>"],
  "strengths": "<what they did well>",
  "improvements": "<what to improve>",
  "ai_generated_flag": <true/false>,
  "ai_generated_reason": "<explanation or empty>",
  "ai_generated_confidence": "<high/medium/low>"
}`;

  const text = await callGemini(prompt, 'adminRescore');
  if (!text) return pendingResult();
  const p = parseJSON(text);
  if (!p) return pendingResult(text);
  return {
    score: Math.min(Math.max(typeof p.score === 'number' ? p.score : 0, 0), 100),
    feedback: p.feedback || '',
    rubric_hits: p.rubric_hits || [],
    rubric_misses: p.rubric_misses || [],
    strengths: p.strengths || '',
    improvements: p.improvements || '',
    ai_generated_flag: p.ai_generated_flag || false,
    ai_generated_reason: p.ai_generated_reason || '',
    ai_generated_confidence: p.ai_generated_confidence || '',
    raw_ai_response: text,
    scoring_pending: false,
  };
}

/* ═══════════════════════════════════════════════════════════════════
   INDIVIDUAL ANALYSIS
   ═══════════════════════════════════════════════════════════════════ */

export async function generateEmployeeAnalysis(attempt, mcqAnswers, scenarioAnswers) {
  if (!GEMINI_KEY) return null;

  const wrongMCQs = mcqAnswers.filter(a => !a.is_correct).map(a =>
    `- [${a.category}] "${a.question_text}" → answered "${a.options?.[a.selected_option]}" (correct: "${a.options?.[a.correct_option]}")`
  ).join('\n');

  const scenarioSummary = scenarioAnswers.map(s => {
    let line = `- ${s.scenario_title} (${s.category}): Score ${s.ai_score ?? 'pending'}% — ${s.ai_feedback}`;
    if (s.ai_generated_flag) line += ` [⚠ AI-GENERATED: ${s.ai_generated_reason}]`;
    return line;
  }).join('\n');

  const aiFlags = scenarioAnswers.filter(s => s.ai_generated_flag);

  const prompt = `Generate individual performance analysis.

RESULTS:
- MCQ: ${attempt.mcq_score}% (${attempt.mcq_correct}/${attempt.mcq_total})
- Written: ${attempt.scenario_score ?? 'pending'}%
- Overall: ${attempt.total_score ?? 'pending'}% (${attempt.passed ? 'PASSED' : 'FAILED'})
- Tab switches: ${attempt.tab_switches}
- Time: ${Math.floor(attempt.time_used_seconds / 60)}m ${attempt.time_used_seconds % 60}s

INCORRECT MCQs:
${wrongMCQs || 'All correct!'}

WRITTEN SCENARIOS:
${scenarioSummary || 'No responses.'}

${aiFlags.length > 0 ? `⚠ AI-GENERATED CONTENT DETECTED in ${aiFlags.length} scenario(s).` : ''}
${attempt.tab_switches > 0 ? `⚠ INTEGRITY: ${attempt.tab_switches} tab switch(es) during test.` : ''}

JSON:
{
  "overall_assessment": "<2-3 sentence assessment>",
  "prompt_readiness_level": "<Beginner|Intermediate|Advanced>",
  "strongest_areas": ["<area>"],
  "weakest_areas": ["<area>"],
  "recommendations": ["<recommendation>", "<recommendation>", "<recommendation>"],
  "training_priority": "<what to focus first>",
  "integrity_concerns": "<concerns or 'None'>",
  "ai_content_warning": <true/false>
}`;

  const text = await callGemini(prompt, 'employeeAnalysis');
  return text ? parseJSON(text) : null;
}

/* ═══════════════════════════════════════════════════════════════════
   TEAM ANALYTICS
   ═══════════════════════════════════════════════════════════════════ */

export async function generateTeamAnalytics(allResults) {
  if (!GEMINI_KEY || !allResults?.length) return null;

  const summary = allResults.map(r => ({
    dept: r.department, mcq: r.mcq_score, written: r.scenario_score,
    total: r.total_score, passed: r.passed, tabs: r.tab_switches,
    ai_flagged: r.ai_flagged || false,
  }));

  const prompt = `Analyze team assessment results for AI readiness program.

DATA (${allResults.length} employees):
${JSON.stringify(summary, null, 2)}

JSON:
{
  "executive_summary": "<3-4 sentence summary for leadership>",
  "overall_readiness": "<Low|Moderate|Good|Strong>",
  "pass_rate_analysis": "<interpretation>",
  "mcq_vs_written_insight": "<theory vs practical gap>",
  "department_insights": [{"dept": "<n>", "insight": "<finding>"}],
  "top_3_organizational_gaps": ["<gap>"],
  "training_recommendations": ["<recommendation>"],
  "integrity_notes": "<tab switches or AI-content patterns>",
  "next_steps": ["<action>"]
}`;

  const text = await callGemini(prompt, 'teamAnalytics');
  return text ? parseJSON(text) : null;
}
